import type { Level } from "building-types/levels/levelTypes";
import type { BalusterDefault, PostPlacementInstance, Railing } from "building-types/settings/railingTypes";
import { RailingInfillStyle } from "building-types/settings/railingTypes";
import { WORLD_ZERO_INCHES } from "common/constants/world";
import type { Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { numAdd, numSub, worldInchesNew } from "common/utils/nominalNumbers";
import { DIST_EPSILON } from "geom/core";
import { extrudeFaceAlongPolyLine3, extrudePolyLine2RotatedBasis } from "geom/extrude";
import type { Geom } from "geom/geom";
import { line2Tangent } from "geom/line2";
import { planeNewXY } from "geom/plane";
import { polyLine2New } from "geom/polyLine2Types";
import {
  polyLine3ByTwoPoints,
  polyLine3EndPoint,
  polyLine3EvalEdge,
  polyLine3StartPoint,
  polyLine3VertexCount,
  polyLine3EdgeTangent,
  polyLine3EdgeStart,
  polyLine3EdgeEnd,
  polyLine3EdgeLength,
} from "geom/polyLine3";
import { rangeInclusiveByMaxStep, rangeNonInclusiveByMaxStep } from "geom/range";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import type { IndexAABB2 } from "geom/rtree";
import { vec2AngleBetween, vec2Dist } from "geom/vec2";
import type RBush from "rbush";
import type { PostInfillControlPoints, InfillSegment } from "./infillTypes";
import { SegmentEndType } from "./infillTypes";
import { getMaxHeightOfSurfacesBelowPoint2, type Path3OnSurfaces, type Surface } from "./surfaces";

export interface RailingInfillGeom {
  readonly geom: Geom;
  readonly segmentIndex: number;
}

interface MakeInfillOptions {
  readonly postPlacementInstances: PostPlacementInstance[];
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly infillDepth: number;
  readonly baluster?: BalusterDefault;
  readonly topRailHeight: WorldInches;
  readonly topRailThicknessResolved: number;
  readonly bottomRailHeight: WorldInches;
  readonly bottomRailThicknessResolved: number;
  readonly postHalfWidth: number;
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
  readonly railing: Railing;
  readonly infillStyle: RailingInfillStyle;
}

export function makeInfill({
  railingPathEdge3s,
  infillDepth,
  baluster,
  topRailHeight,
  topRailThicknessResolved,
  bottomRailHeight,
  bottomRailThicknessResolved,
  postPlacementInstances,
  postHalfWidth,
  level,
  levels,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
  railing,
  infillStyle,
}: MakeInfillOptions): RailingInfillGeom[] {
  const railingPath3Count = railingPathEdge3s.length;
  const railingEdgeLengths: { [edgeIndex: number]: number } = {};
  const halfBottomRailThicknessResolvedInches: WorldInches = worldInchesNew(bottomRailThicknessResolved / 2);
  const halfTopRailThicknessResolvedInches: WorldInches = worldInchesNew(topRailThicknessResolved / 2);

  for (let i = 0; i < railingPath3Count; i++) {
    const { polyLine3: pl3 } = railingPathEdge3s[i]; // removed pathSurfaceOverlaps as the updated functionality doesn't seem to require it.
    if (!pl3) {
      continue;
    }
    const [startx, starty] = polyLine3StartPoint(pl3);
    const [endx, endy] = polyLine3EndPoint(pl3);
    railingEdgeLengths[i] = vec2Dist(startx, starty, endx, endy);
  }

  const postInfillControlPoints = makePostInfillControlPointsFromPostPlacements(
    railingEdgeLengths,
    railingPath3Count,
    railingPathEdge3s,
    postPlacementInstances,
  );

  const infillSegments = makeInfillSegmentsFromControlPoints(postInfillControlPoints, railingEdgeLengths);

  if (infillStyle === RailingInfillStyle.SOLID) {
    return makeSolidInfillGeoms({
      railingPathEdge3s,
      infillDepth,
      topRailHeight,
      halfTopRailThicknessResolvedInches,
      bottomRailHeight,
      halfBottomRailThicknessResolvedInches,
      infillSegments,
    }).map((geom) => ({ geom, segmentIndex: geom.segmentIndex }));
  }

  if (infillStyle === RailingInfillStyle.BALUSTERS && baluster) {
    return makeBalusterInfillGeoms({
      railingPathEdge3s,
      railingPath3Count,
      infillDepth,
      baluster,
      topRailHeight,
      halfTopRailThicknessResolvedInches,
      halfBottomRailThicknessResolvedInches,
      postHalfWidth,
      level,
      levels,
      levelStairSurfaceRTree,
      levelStairSurfaces,
      levelPerimeterSurfaceRTree,
      levelPerimeterSurfaces,
      railing,
      infillSegments,
    });
  }

  return [];
}

interface MakeSolidInfillGeomsOptions {
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly infillDepth: number;
  readonly topRailHeight: WorldInches;
  readonly halfTopRailThicknessResolvedInches: WorldInches;
  readonly bottomRailHeight: WorldInches;
  readonly halfBottomRailThicknessResolvedInches: WorldInches;
  readonly infillSegments: InfillSegment[];
}

type SegmentedRailingInfillGeom = Geom & {
  readonly segmentIndex: number;
};

function makeSolidInfillGeoms({
  railingPathEdge3s,
  infillDepth,
  topRailHeight,
  halfTopRailThicknessResolvedInches,
  bottomRailHeight,
  halfBottomRailThicknessResolvedInches,
  infillSegments,
}: MakeSolidInfillGeomsOptions): SegmentedRailingInfillGeom[] {
  const infillGeoms: SegmentedRailingInfillGeom[] = [];

  const halfWidth = infillDepth / 2;
  const topHeight = topRailHeight - halfTopRailThicknessResolvedInches;
  const bottomHeight = bottomRailHeight - halfBottomRailThicknessResolvedInches;

  const profile = [
    polyLine2New([
      -halfWidth,
      bottomHeight,
      -halfWidth,
      topHeight,
      halfWidth,
      topHeight,
      halfWidth,
      bottomHeight,
      -halfWidth,
      bottomHeight,
    ]),
  ];
  const segmentIndexOffsets = makeSegmentIndexOffsets(railingPathEdge3s);

  // Go through each infill segment and find the overlapping sub-edges.
  for (let i = 0; i < infillSegments.length; i++) {
    const { edgeIndex, startParam: infillStartT, endParam: infillEndT, spanIncludesInfill } = infillSegments[i];
    if (!spanIncludesInfill) {
      continue;
    }

    const { polyLine3: edge } = railingPathEdge3s[edgeIndex];
    if (!edge) {
      continue;
    }

    // The t-value measures the 2D distance along the edge.
    let startT = 0;
    let endT = 0;

    // The edge is made of sub-edges which are collinear in 2D, but may change slope in 3D.
    // For each sub-edge, find how much it overlaps with the infill segment and extrude that portion.
    const subEdgeCount = polyLine3VertexCount(edge) - 1;
    for (let j = 0; j < subEdgeCount; j++) {
      startT = endT;
      const [subEdgeStartX, subEdgeStartY] = polyLine3EdgeStart(edge, j);
      const [subEdgeEndX, subEdgeEndY] = polyLine3EdgeEnd(edge, j);
      const subEdgeLength2D = vec2Dist(subEdgeStartX, subEdgeStartY, subEdgeEndX, subEdgeEndY);
      endT = startT + subEdgeLength2D;

      if (endT <= infillStartT || startT >= infillEndT) {
        // The sub-edge does not overlap with the infill segment.
        continue;
      }

      // Clamp to the bounds of the infill segment.
      const clampedStartT = Math.max(infillStartT, startT);
      const clampedEndT = Math.min(infillEndT, endT);

      const subEdgeLength3D = polyLine3EdgeLength(edge, j);
      const ratio2Dto3D = subEdgeLength3D / subEdgeLength2D;

      // Find the 2D distance from the start and convert to 3D.
      const [pathSX, pathSY, pathSZ] = polyLine3EvalEdge(edge, j, (clampedStartT - startT) * ratio2Dto3D, false);
      const [pathEX, pathEY, pathEZ] = polyLine3EvalEdge(edge, j, (clampedEndT - startT) * ratio2Dto3D, false);

      const path = polyLine3ByTwoPoints(pathSX, pathSY, pathSZ, pathEX, pathEY, pathEZ);
      const [nx, ny] = line2Tangent(pathSX, pathSY, pathEX, pathEY);

      infillGeoms.push(
        Object.assign(extrudeFaceAlongPolyLine3(profile, path, 0, 0, 1, [nx, ny, 0], [nx, ny, 0]), {
          segmentIndex: segmentIndexOffsets[edgeIndex] + j,
        }),
      );
    }
  }

  return infillGeoms;
}

interface MakeBalusterInfillGeomsOptions {
  readonly railingPathEdge3s: Path3OnSurfaces[];
  readonly railingPath3Count: number;
  readonly infillDepth: number;
  readonly baluster: BalusterDefault;
  readonly topRailHeight: WorldInches;
  readonly halfTopRailThicknessResolvedInches: WorldInches;
  readonly halfBottomRailThicknessResolvedInches: WorldInches;
  readonly postHalfWidth: number;
  readonly level: Level;
  readonly levels: Table<Level>;
  readonly levelStairSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelStairSurfaces: Table<Surface[]>;
  readonly levelPerimeterSurfaceRTree: Table<RBush<IndexAABB2>>;
  readonly levelPerimeterSurfaces: Table<Surface[]>;
  readonly railing: Railing;
  readonly infillSegments: InfillSegment[];
}

function makeBalusterInfillGeoms({
  railingPathEdge3s,
  railingPath3Count,
  infillDepth,
  baluster,
  topRailHeight,
  halfTopRailThicknessResolvedInches,
  halfBottomRailThicknessResolvedInches,
  postHalfWidth,
  level,
  levels,
  levelStairSurfaceRTree,
  levelStairSurfaces,
  levelPerimeterSurfaceRTree,
  levelPerimeterSurfaces,
  railing,
  infillSegments,
}: MakeBalusterInfillGeomsOptions): RailingInfillGeom[] {
  const infillGeoms: RailingInfillGeom[] = [];

  // the distance the start and/or end of an infill segment is adjusted to keep consistent infill spacing at posts
  const edgeAdjustment = postHalfWidth - baluster.width / 2;
  let balusterSpacing = baluster.spacing;
  if (balusterSpacing < baluster.width) {
    balusterSpacing = baluster.width;
  }
  const balusterParamsPerEdge = makeBalusterParamsFromInfillSegments(infillSegments, balusterSpacing, edgeAdjustment);

  const balusterStartHeightGreaterThanZero = baluster.startHeight > DIST_EPSILON;
  const balusterStartHeight: WorldInches = worldInchesNew(baluster.startHeight);
  const segmentIndexOffsets = makeSegmentIndexOffsets(railingPathEdge3s);

  for (let index = 0; index < railingPath3Count; index++) {
    const { polyLine3: pl3 } = railingPathEdge3s[index];
    if (!pl3) {
      continue;
    }

    const balusterParams = balusterParamsPerEdge[index];

    const vertexCount = polyLine3VertexCount(pl3);
    const [startx, starty] = polyLine3StartPoint(pl3);

    // polyLine3edges are collinear in 2D but some edges have slope in 3D
    for (let j = 0; j < vertexCount - 1; j++) {
      const [tx, ty] = polyLine3EdgeTangent(pl3, j < vertexCount - 1 ? j : j - 1);

      const [sx, sy] = polyLine3EdgeStart(pl3, j);
      const [ex, ey] = polyLine3EdgeEnd(pl3, j);
      const dist3 = polyLine3EdgeLength(pl3, j);
      const dist2 = vec2Dist(sx, sy, ex, ey);

      // ratio of the 3D distance to the 2D distance.
      const dist3Ratio = dist3 / dist2;
      const startDist = vec2Dist(startx, starty, sx, sy);
      const rotation = vec2AngleBetween(0, 1, tx, ty);

      // due to the slope of the pl3, the baluster param may need to be adjusted to be on the edge of the pl3.
      for (const div of (balusterParams || []).filter((x) => x >= startDist && x <= startDist + dist2)) {
        const [x, y, z] = polyLine3EvalEdge(pl3, j, (div - startDist) * dist3Ratio, false);
        const worldZ = worldInchesNew(z);

        // if balusterStartHeight is zero, then sit on each stair or surface; otherwise, sit relative to the bottom rail slope.
        const height = balusterStartHeightGreaterThanZero
          ? WORLD_ZERO_INCHES
          : getMaxHeightOfSurfacesBelowPoint2({
              level,
              levels,
              x,
              y,
              z: worldZ,
              ignoreStairsAndRoomsAbove: railing.ignoreStairsAndRoomsAbove,
              levelStairSurfaceRTree,
              levelStairSurfaces,
              levelPerimeterSurfaceRTree,
              levelPerimeterSurfaces,
            });

        const bottomZ: WorldInches = balusterStartHeightGreaterThanZero
          ? numAdd(worldZ, numSub(balusterStartHeight, halfBottomRailThicknessResolvedInches))
          : height;
        const resolvedHeight: WorldInches = balusterStartHeightGreaterThanZero
          ? numSub(
              numSub(numAdd(topRailHeight, halfBottomRailThicknessResolvedInches), halfTopRailThicknessResolvedInches),
              balusterStartHeight,
            )
          : numSub(numAdd(numSub(worldZ, height), topRailHeight), halfTopRailThicknessResolvedInches);

        const profile = rect2ByCenterWidthHeight(0, 0, infillDepth, baluster.width);
        infillGeoms.push({
          geom: extrudePolyLine2RotatedBasis(profile, planeNewXY(x, y, bottomZ), 0, 0, resolvedHeight, rotation),
          segmentIndex: segmentIndexOffsets[index] + j,
        });
      }
    }
  }

  return infillGeoms;
}

function makeSegmentIndexOffsets(railingPathEdge3s: Path3OnSurfaces[]): number[] {
  const offsets: number[] = [];
  let segmentIndex = 0;
  for (const { polyLine3 } of railingPathEdge3s) {
    offsets.push(segmentIndex);
    segmentIndex += polyLine3 ? polyLine3VertexCount(polyLine3) - 1 : 1;
  }
  return offsets;
}

function makePostInfillControlPointsFromPostPlacements(
  railingEdgeLengths: { [edgeIndex: number]: number },
  railingPath3Count: number,
  railingPathEdge3s: Path3OnSurfaces[],
  postPlacementInstances: PostPlacementInstance[],
) {
  const postInfillControlPoints = groupPostPlacementsIntoControlPoints(
    railingEdgeLengths,
    railingPath3Count,
    railingPathEdge3s,
    postPlacementInstances,
  );

  optimizeControlPointsForLookup(postInfillControlPoints);

  solveInfillRemoval(postInfillControlPoints);

  return postInfillControlPoints;
}

/**
 * Since users can place posts anywhere this function merges columns together if they are at the
 * same position within tolerance, they can share controlling infill, etc and removes the need
 * to solve for zero length segments, etc.
 */
function groupPostPlacementsIntoControlPoints(
  railingEdgeLengths: { [edgeIndex: number]: number },
  railingPath3Count: number,
  railingPathEdge3s: Path3OnSurfaces[],
  postPlacementInstances: PostPlacementInstance[],
) {
  const postInfillControlPoints: PostInfillControlPoints[] = [];
  const endControlPoint: PostInfillControlPoints = {
    param: railingEdgeLengths[railingPath3Count - 1],
    edgeIndex: railingPath3Count - 1,
    postPlacements: [],
  };

  for (let i = 0; i < railingPath3Count; i++) {
    const { polyLine3: pl3 } = railingPathEdge3s[i];
    if (!pl3) {
      continue;
    }

    const startControlPoint: PostInfillControlPoints = {
      param: 0,
      edgeIndex: i,
      postPlacements: [],
    };

    const midControlPoints: PostInfillControlPoints[] = [];

    for (const instance of postPlacementInstances) {
      const { edgeIndex, param: instanceParam } = instance;

      if (edgeIndex < i - 1) {
        continue;
      }

      if (edgeIndex > i) {
        break;
      }

      // start points from previous edge end
      if (edgeIndex === i - 1 && Math.abs(railingEdgeLengths[edgeIndex] - instanceParam) < DIST_EPSILON) {
        startControlPoint.postPlacements.push(instance);
      }

      // points that might land at start or middle
      if (edgeIndex === i) {
        const atStart = instanceParam < DIST_EPSILON;
        if (atStart) {
          startControlPoint.postPlacements.push(instance);
          continue;
        }

        const atEnd = Math.abs(railingEdgeLengths[edgeIndex] - instanceParam) < DIST_EPSILON;
        if (atEnd) {
          // only place end control point at the end of the entire path
          if (i === railingPath3Count - 1) {
            endControlPoint.postPlacements.push(instance);
          }
          continue;
        }

        let foundMatch = false;
        let largerThanIndex = 0;
        for (const pointIndex of Object.keys(midControlPoints)) {
          const point = midControlPoints[pointIndex];
          const difference = instanceParam - point.param;
          if (difference >= DIST_EPSILON) {
            largerThanIndex = +pointIndex;
          }
          if (Math.abs(instanceParam - point.param) < DIST_EPSILON) {
            point.postPlacements.push(instance);
            foundMatch = true;
            break;
          }
        }

        if (!foundMatch) {
          midControlPoints.splice(largerThanIndex + 1, 0, {
            param: instance.param,
            edgeIndex: i,
            postPlacements: [instance],
          });
        }
      }
    }

    postInfillControlPoints.push(startControlPoint);
    if (midControlPoints.length) {
      postInfillControlPoints.push(...midControlPoints);
    }
  }
  postInfillControlPoints.push(endControlPoint);
  return postInfillControlPoints;
}

function optimizeControlPointsForLookup(postInfillControlPoints: PostInfillControlPoints[]) {
  for (const point of postInfillControlPoints) {
    point.hasPost = !!point.postPlacements.length;
    point.removeInfillBefore = point.postPlacements.length
      ? point.postPlacements.findIndex((x) => x.removeInfillBefore) !== -1
      : false;
    point.removeInfillAfter = point.postPlacements.length
      ? point.postPlacements.findIndex((x) => x.removeInfillAfter) !== -1
      : false;
  }
}

/**
 * Marches forward solving whether infill should be removed for a given span
 */
function solveInfillRemoval(postInfillControlPoints: PostInfillControlPoints[]) {
  let i = 0;
  while (i < postInfillControlPoints.length - 1) {
    const point = postInfillControlPoints[i];

    // already solved, go on to the next control point
    if (point.hasInfill !== undefined) {
      i++;
      continue;
    }

    let nextPostIndex = i;

    //forward looking, excluding the current
    for (let f = i + 1; f < postInfillControlPoints.length; f++) {
      // we look starting at the next span to see if the control point has a post
      const fPoint = postInfillControlPoints[f];
      if (fPoint.hasPost) {
        // if it does we stop our march at the index prior
        nextPostIndex = f - 1;
        break;
      }
    }

    let shouldRemoveInfillInSpan = false;
    const postPrevious = postInfillControlPoints[i];
    if (postPrevious.removeInfillAfter) {
      shouldRemoveInfillInSpan = true;
    }
    // if we already know the span is marked for no infill from the previous post, there's no need to check the forward post
    if (!shouldRemoveInfillInSpan) {
      // we need to look back at the post after our span to see if we should remove posts for the span
      const postNext = postInfillControlPoints[nextPostIndex + 1];
      if (postNext.removeInfillBefore) {
        shouldRemoveInfillInSpan = true;
      }
    }

    // mark whether all control points in span have infill or not
    for (let s = i; s < nextPostIndex + 1; s++) {
      const post = postInfillControlPoints[s];
      post.hasInfill = !shouldRemoveInfillInSpan;
    }

    // set start for next loop
    i = nextPostIndex + 1;
  }
}

function makeInfillSegmentsFromControlPoints(
  postInfillControlPoints: PostInfillControlPoints[],
  railingEdgeLengths: { [edgeIndex: number]: number },
) {
  const infillSegmentsImproved: InfillSegment[] = [];
  for (let j = 0; j < postInfillControlPoints.length - 1; j++) {
    const { edgeIndex, param, hasPost, hasInfill } = postInfillControlPoints[j];
    const { edgeIndex: nextEdgeIndex, param: nextParam, hasPost: nextHasPost } = postInfillControlPoints[j + 1];

    const resolvedNextParam = nextEdgeIndex > edgeIndex ? railingEdgeLengths[edgeIndex] : nextParam;

    infillSegmentsImproved.push({
      edgeIndex,
      startParam: param,
      startType: hasPost ? SegmentEndType.POST : SegmentEndType.BALUSTER,
      endParam: resolvedNextParam,
      endType: nextHasPost ? SegmentEndType.POST : SegmentEndType.BALUSTER,
      endShouldPlaceInfill: j === postInfillControlPoints.length - 2 && !nextHasPost,
      spanIncludesInfill: !!hasInfill,
    });
  }
  return infillSegmentsImproved;
}

/**
 * Depending on the start and end conditions of a infill segment, the array of infill params
 * is adjusted to keep consistent spacing.
 */
export function makeBalusterParamsFromInfillSegments(
  infillSegments: InfillSegment[],
  balusterSpacing: number,
  postParamAdjustment: number,
) {
  const balusterParamsPerEdge: { [edgeIndex: number]: number[] } = {};

  for (const {
    edgeIndex,
    startParam,
    endParam,
    startType,
    endType,
    endShouldPlaceInfill,
    spanIncludesInfill,
  } of infillSegments) {
    const infillInstanceParams: number[] = balusterParamsPerEdge[edgeIndex] || [];
    if (!spanIncludesInfill) {
      continue;
    }
    const startOffset = startType === SegmentEndType.POST ? postParamAdjustment : 0;
    const start = startParam + startOffset;
    const endOffset = endType === SegmentEndType.POST ? postParamAdjustment : 0;
    const end = endParam - endOffset;
    const startInclusive = startType !== SegmentEndType.POST;
    const endInclusive = endShouldPlaceInfill && endType !== SegmentEndType.POST;

    if (startInclusive && endInclusive) {
      infillInstanceParams.push(...rangeInclusiveByMaxStep(start, end, balusterSpacing));
    } else if (startInclusive) {
      const items = rangeInclusiveByMaxStep(start, end, balusterSpacing);
      items.pop();
      infillInstanceParams.push(...items);
    } else if (endInclusive) {
      const items = rangeInclusiveByMaxStep(start, end, balusterSpacing);
      items.shift();
      infillInstanceParams.push(...items);
    } else {
      const diff = Math.abs(start - end);
      if (diff > DIST_EPSILON) {
        infillInstanceParams.push(...rangeNonInclusiveByMaxStep(start, end, balusterSpacing));
      }
    }
    balusterParamsPerEdge[edgeIndex] = infillInstanceParams;
  }
  return balusterParamsPerEdge;
}
